// src/pages/Catalog.js

import React, { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import './Catalog.css';
import './Home.css';
import '../styles/pageAnimation.css';
// Use admin grid style
// ProductCard not used in this page
import DashboardHeader from '../components/DashboardHeader';
import TermsModal from '../components/TermsModal';
import Toast from '../components/Toast';
import ConfirmModal from '../components/ConfirmModal';

function Catalog() {
  const location = useLocation();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(() => {
    // Initialize category from URL on first load
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get('category') || '';
  });

  // Scroll to top when component mounts or location changes - aggressive approach
  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [location.pathname, location.search]);
  
  // Initialize special filter from URL
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const filter = searchParams.get('filter');
    if (filter && ['newArrivals', 'featured', 'topSelling'].includes(filter)) {
      setSpecialFilter(filter);
      // Clear other filters when special filter is active
      setSelectedCategory('');
      setMinPrice('');
      setMaxPrice('');
      setSearchQuery('');
      // onSaleOnly removed; ensure other filters reset
      setInStockOnly(false);
    } else {
      setSpecialFilter('');
    }
  }, [location.search]);
  const [page, setPage] = useState(1);
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [filterOpen, setFilterOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  // Removed onSaleOnly state (filter UI removed)
  const [inStockOnly, setInStockOnly] = useState(false);
  const [sortBy, setSortBy] = useState('alphabetically-az');
  const [maxPriceInDb, setMaxPriceInDb] = useState(0);
  const [specialFilter, setSpecialFilter] = useState(() => {
    // Initialize special filter from URL on first load
    const searchParams = new URLSearchParams(location.search);
    const filter = searchParams.get('filter');
    if (filter && ['newArrivals', 'featured', 'topSelling'].includes(filter)) {
      return filter;
    }
    return '';
  });
  const [isMobile, setIsMobile] = useState(false);
  const [showGreenBar, setShowGreenBar] = useState(true);
  const [itemsPerPage, setItemsPerPage] = useState(12);
  const [categories, setCategories] = useState([]);
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
  const [sortDropdownOpen, setSortDropdownOpen] = useState(false);
  const [addingToCart, setAddingToCart] = useState({});
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);

  // Load global category order from backend (fallback to localStorage/defaults)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API}/api/products/categories/order`);
        if (res.ok) {
          const data = await res.json();
          if (!cancelled) {
            const fromApi = Array.isArray(data.order) ? data.order : [];
            if (fromApi.length) return setCategories(fromApi);
          }
        }
      } catch {}
      // fallback to localStorage if exists
      try {
        const saved = JSON.parse(localStorage.getItem('adminCategories') || '[]');
        if (!cancelled && Array.isArray(saved) && saved.length) setCategories(saved);
      } catch {}
    })();
    return () => { cancelled = true; };
  }, []);

  // Fetch cart items
  const fetchCart = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API}/api/cart`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCartItems(data.items?.map(item => item.product?._id).filter(Boolean) || []);
      }
    } catch (error) {
      console.error('Error fetching cart:', error);
    }
  };

  useEffect(() => {
    fetchCart();
  }, []);

  // Quick add/remove cart function
  const handleQuickAddToCart = async (e, productId, productName) => {
    e.preventDefault();
    e.stopPropagation();
    
    const token = localStorage.getItem('token');
    if (!token) {
      setShowLoginModal(true);
      return;
    }

    const isInCart = cartItems.includes(productId);
    setAddingToCart(prev => ({ ...prev, [productId]: true }));

    try {
      if (isInCart) {
        // Remove from cart
        const response = await fetch(`${process.env.REACT_APP_API}/api/cart/${productId}`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          }
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.message || 'Failed to remove from cart');
        }

        setCartItems(prev => prev.filter(id => id !== productId));
        setToastMessage(`${productName} removed from cart!`);
        setShowToast(true);
        // Dispatch cart update event
        window.dispatchEvent(new Event('cartUpdated'));
      } else {
        // Add to cart
        const response = await fetch(`${process.env.REACT_APP_API}/api/cart`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ productId, quantity: 1 })
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.message || 'Failed to add to cart');
        }

        setCartItems(prev => [...prev, productId]);
        setToastMessage(`${productName} added to cart!`);
        setShowToast(true);
        // Dispatch cart update event
        window.dispatchEvent(new Event('cartUpdated'));
      }
    } catch (error) {
      console.error('Cart operation error:', error);
      setToastMessage(error.message || 'Failed to update cart');
      setShowToast(true);
    } finally {
      setAddingToCart(prev => ({ ...prev, [productId]: false }));
    }
  };

  // Redirect admins to product management
  useEffect(() => {
    const role = localStorage.getItem('role');
    const isAdmin = localStorage.getItem('isAdmin') === 'true';
    if (role === 'admin' || isAdmin) {
      navigate('/admin/products', { replace: true });
    }
  }, [navigate]);

  // Update category when URL changes
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const categoryFromUrl = searchParams.get('category') || '';
    // Always sync selectedCategory from URL when the URL changes.
    // This avoids referencing `selectedCategory` inside this effect
    // (which previously caused an eslint missing dependency warning).
    setSelectedCategory(categoryFromUrl);
    // Reset to first page when URL-based category changes
    setPage(1);
  }, [location.search]);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300); // 300ms delay

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch products using useCallback to prevent infinite loops
  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      
      // Use server-side pagination and sorting - responsive: 8 mobile (2x4), 12 desktop (4x3)
      const itemsPerPageValue = isMobile ? 8 : 12;
      params.append('limit', itemsPerPageValue);
      params.append('page', page);
      
      // Handle special filters using regular endpoint with filters
      if (specialFilter === 'newArrivals') {
        params.append('sort', 'createdAt-desc');
      } else if (specialFilter === 'featured') {
        params.append('featured', 'true');
      } else if (specialFilter === 'topSelling') {
        params.append('sort', 'salesCount-desc');
      }
      
      // Regular filtering
      if (selectedCategory) params.append('category', selectedCategory);
      if (minPrice) params.append('min', minPrice);
      if (maxPrice) params.append('max', maxPrice);
      if (debouncedSearchQuery.trim()) params.append('search', debouncedSearchQuery.trim());
      // onSaleOnly filter removed
      if (inStockOnly) params.append('inStock', 'true');
      
      // Map frontend sortBy to backend sort parameter (only if no special filter is active)
      if (!specialFilter) {
        const sortMapping = {
          'alphabetically-az': 'name-asc',
          'alphabetically-za': 'name-desc',
          'price-low-high': 'price-asc',
          'price-high-low': 'price-desc',
          'date-old-new': 'createdAt-asc',
          'date-new-old': 'createdAt-desc',
          'rating-high-low': 'rating-desc',
          'reviews-high-low': 'reviews-desc',
          'newest-first': 'createdAt-desc',
          'on-sale': 'discount-desc'
        };
        const mappedSort = sortMapping[sortBy] || 'name-asc';
        params.append('sort', mappedSort);
      }
      
      console.log('Catalog Fetch Params:', { 
        sortBy, 
        specialFilter, 
        sortParam: params.get('sort'),
        page: params.get('page'),
        limit: params.get('limit'),
        fullURL: `${process.env.REACT_APP_API}/api/products?${params.toString()}`
      });
      const res = await fetch(`${process.env.REACT_APP_API}/api/products?${params.toString()}`);
      if (!res.ok) {
        console.error('Failed to fetch products:', res.status, res.statusText);
        setProducts([]);
        setTotalPages(1);
        setLoading(false);
        return;
      }
      const data = await res.json();
      
      // Backend returns pre-sorted and paginated data
      setProducts(data.products || []);
      setTotalPages(data.totalPages || 1);
      setTotalCount(data.totalCount || 0);
      setItemsPerPage(itemsPerPageValue);
      setLoading(false)
    } catch (err) {
      console.error('Error fetching products:', err);
      setProducts([]);
      setTotalPages(1);
      setLoading(false);
    }
  }, [page, selectedCategory, minPrice, maxPrice, debouncedSearchQuery, inStockOnly, sortBy, isMobile, specialFilter]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  // Fetch highest price on mount
  useEffect(() => {
    const fetchMaxPrice = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API}/api/products/stats/max-price`);
        const data = await res.json();
        setMaxPriceInDb(data.maxPrice || 0);
      } catch (err) {
        console.error('Error fetching max price:', err);
      }
    };
    fetchMaxPrice();
  }, []);

  // Mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Hide green bar on scroll (no animation, instant show/hide)
  useEffect(() => {
    const handleScroll = () => {
      setShowGreenBar(window.scrollY === 0);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div style={{ background: 'linear-gradient(180deg, #f8fdf9 0%, #f0f7f1 100%)', minHeight: '100vh', width: '100vw', display: 'flex', flexDirection: 'column' }}>
      {/* Green Header Bar (thinner) - only visible at top */}
      {showGreenBar && (
        <div style={{ width: '100%', background: 'linear-gradient(90deg, #175c36 0%, #1a6b3f 100%)', minHeight: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(23, 92, 54, 0.2)' }}>
          {/* Empty green bar for accent */}
        </div>
      )}
      {/* Navbar always sticky at top */}
      <div style={{ 
        width: '100%', 
        background: 'rgba(255, 255, 255, 0.98)', 
        backdropFilter: 'blur(10px)', 
        boxShadow: '0 4px 16px rgba(0,0,0,0.10)', 
        position: 'sticky',
        top: 0, 
        zIndex: 9998,
        borderBottom: '1px solid rgba(0, 0, 0, 0.06)'
      }}>
        <div style={{ width: '100%', maxWidth: '1800px', margin: '0 auto', background: 'transparent' }}>
          <DashboardHeader />
        </div>
      </div>
      <div style={{ width: '100%', flex: 1 }} className="page-content-fade-in">
        <div style={{ width: '100%', maxWidth: '1800px', margin: '0 auto', padding: '0 2vw', paddingTop: '40px' }}>
        {/* Search Bar - Desktop (Above sidebar and grid) */}
        <motion.div 
          className="desktop-search-bar-top" 
          style={{ marginBottom: 20, width: '100%' }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <motion.div 
            style={{ 
              position: 'relative', 
              maxWidth: '100%',
              background: '#fff',
              borderRadius: 50,
              boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
              border: '2px solid #f0f0f0',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = '0 6px 24px rgba(122, 156, 58, 0.15)';
              e.currentTarget.style.borderColor = '#7a9c3a';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.08)';
              e.currentTarget.style.borderColor = '#f0f0f0';
            }}
          >
            <div style={{ 
              position: 'absolute', 
              left: 20, 
              top: '50%', 
              transform: 'translateY(-50%)',
              color: '#999',
              pointerEvents: 'none',
              display: 'flex',
              alignItems: 'center'
            }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.35-4.35"></path>
              </svg>
            </div>
            <input 
              type="text"
              placeholder="Search for plants, seeds, pots, and more..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPage(1);
              }}
              style={{
                width: '100%',
                padding: '16px 60px 16px 55px',
                border: 'none',
                borderRadius: 50,
                fontSize: '1rem',
                outline: 'none',
                boxSizing: 'border-box',
                background: 'transparent',
                color: '#333',
                fontWeight: 400
              }}
            />
            {searchQuery && (
              <button
                onClick={() => {
                  setSearchQuery('');
                  setPage(1);
                }}
                style={{
                  position: 'absolute',
                  right: 20,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: '#e0e0e0',
                  border: 'none',
                  borderRadius: '50%',
                  width: 28,
                  height: 28,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.9rem',
                  color: '#666',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#7a9c3a';
                  e.currentTarget.style.color = '#fff';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#e0e0e0';
                  e.currentTarget.style.color = '#666';
                }}
              >
                ✕
              </button>
            )}
          </motion.div>
        </motion.div>

        {/* Filter Toggle and Search Bar (Mobile Only) */}
        <motion.div 
          className="mobile-filter-search-container" 
          style={{ display: 'none', gap: 10, marginBottom: 12, alignItems: 'stretch' }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <motion.button 
            className="filter-toggle-btn"
            onClick={() => setFilterOpen(!filterOpen)}
            whileHover={{ scale: 1.02, y: -2 }}
            whileTap={{ scale: 0.98 }}
            transition={{ duration: 0.2 }}
            style={{
              padding: '0 24px',
              height: '48px',
              minHeight: '48px',
              background: 'linear-gradient(135deg, #7a9c3a 0%, #6a8a32 100%)',
              color: '#fff',
              border: 'none',
              borderRadius: 24,
              fontWeight: 600,
              fontSize: '0.9rem',
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(122, 156, 58, 0.25)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              whiteSpace: 'nowrap',
              flexShrink: 0,
              transition: 'all 0.3s ease',
              minWidth: '110px',
              touchAction: 'manipulation',
              WebkitTapHighlightColor: 'transparent'
            }}
          >
            <span style={{ fontSize: '1.1rem' }}>{filterOpen ? '✕' : '☰'}</span>
            FILTER
          </motion.button>
          
          <motion.div 
            style={{ 
              position: 'relative', 
              flex: 1,
              height: '48px',
              background: '#fff',
              borderRadius: 50,
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              border: '2px solid #f0f0f0',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center'
            }}
            whileFocus={{ scale: 1.01 }}
            transition={{ duration: 0.2 }}
          >
            <div style={{ 
              position: 'absolute', 
              left: 16, 
              top: '50%', 
              transform: 'translateY(-50%)',
              color: '#999',
              pointerEvents: 'none',
              display: 'flex',
              alignItems: 'center'
            }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.35-4.35"></path>
              </svg>
            </div>
            <input 
              type="text"
              placeholder="Search products..."
              className="mobile-search-input"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPage(1);
              }}
              style={{
                width: '100%',
                height: '100%',
                padding: '0 50px 0 45px',
                border: 'none',
                borderRadius: 50,
                fontSize: '0.9rem',
                outline: 'none',
                background: 'transparent',
                boxSizing: 'border-box',
                touchAction: 'manipulation',
                WebkitTapHighlightColor: 'transparent'
              }}
            />
            {searchQuery && (
              <motion.button
                onClick={() => {
                  setSearchQuery('');
                  setPage(1);
                }}
                whileHover={{ scale: 1.1, rotate: 90 }}
                whileTap={{ scale: 0.95 }}
                transition={{ duration: 0.2 }}
                style={{
                  position: 'absolute',
                  right: 16,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  color: '#999',
                  cursor: 'pointer',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  fontSize: '1.2rem',
                  touchAction: 'manipulation',
                  WebkitTapHighlightColor: 'transparent'
                }}
              >
                ×
              </motion.button>
            )}
          </motion.div>
        </motion.div>

        <div style={{ display: 'flex', gap: 24, width: '100%' }}>
          {/* Backdrop overlay for mobile */}
          <AnimatePresence>
            {filterOpen && (
              <motion.div 
                className="filter-backdrop"
                onClick={() => setFilterOpen(false)}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                style={{
                  display: 'none',
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'rgba(0,0,0,0.4)',
                  zIndex: 999
                }}
              />
            )}
          </AnimatePresence>

          {/* Sidebar */}
          <motion.div 
            className={`catalog-sidebar ${filterOpen ? 'mobile-open' : ''}`}
            initial={false}
            animate={filterOpen ? { y: 0, opacity: 1 } : {}}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            style={{ 
              minWidth: 200, 
              maxWidth: 260, 
              flex: '0 0 240px', 
              background: '#ffffff', 
              borderRadius: 20, 
              boxShadow: '0 8px 32px rgba(0,0,0,0.08)', 
              padding: '8px 0 20px 0', 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              minHeight: 'auto', 
              position: 'relative',
              border: '1px solid rgba(122, 156, 58, 0.1)'
            }}>
            
            {/* Mobile Drawer Header */}
            <div className="mobile-drawer-header" style={{
              display: 'none',
              width: '100%',
              padding: '20px 24px',
              borderBottom: '1px solid rgba(0,0,0,0.06)',
              marginBottom: '20px',
              background: 'linear-gradient(135deg, #f8fdf9 0%, #ffffff 100%)'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '8px'
              }}>
                <h2 style={{
                  fontSize: '1.5rem',
                  fontWeight: 700,
                  color: '#2d3436',
                  letterSpacing: '-0.5px',
                  margin: 0
                }}>Filters</h2>
                <motion.button 
                  className="sidebar-close-btn"
                  onClick={() => setFilterOpen(false)}
                  whileTap={{ scale: 0.9 }}
                  transition={{ duration: 0.15 }}
                  style={{
                    background: 'rgba(122, 156, 58, 0.1)',
                    border: 'none',
                    borderRadius: '50%',
                    width: '44px',
                    height: '44px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    color: '#2d3436',
                    fontSize: '1.3rem',
                    fontWeight: 300,
                    touchAction: 'manipulation',
                    WebkitTapHighlightColor: 'transparent',
                    boxShadow: '0 2px 8px rgba(122, 156, 58, 0.15)'
                  }}
                >
                  ✕
                </motion.button>
              </div>
              <div style={{
                width: '40px',
                height: '4px',
                background: '#7a9c3a',
                borderRadius: '2px',
                marginTop: '4px'
              }}></div>
            </div>

            {/* Categories - FIRST */}
            <motion.div 
              className="filter-section"
              style={{ width: '90%', marginBottom: 32, marginTop: 0 }}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.1 }}
            >
              <h3 style={{ 
                fontWeight: 700, 
                fontSize: '1.15rem', 
                marginBottom: 20, 
                color: '#2d3436',
                letterSpacing: '-0.3px',
                paddingBottom: 0,
                position: 'relative'
              }}>
                Special Collections
              </h3>
              <div style={{ marginTop: 0, display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <motion.div
                  key="newArrivals"
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: 0.15 }}
                  whileTap={{ scale: 0.97 }}
                  className="filter-option-mobile"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '16px 18px',
                    cursor: 'pointer',
                    background: specialFilter === 'newArrivals' ? 'linear-gradient(135deg, #e9f5e1 0%, #f1f9ed 100%)' : '#fafafa',
                    fontWeight: specialFilter === 'newArrivals' ? 600 : 500,
                    color: specialFilter === 'newArrivals' ? '#2d5016' : '#555',
                    borderRadius: 14,
                    border: '2px solid',
                    borderColor: specialFilter === 'newArrivals' ? '#7a9c3a' : 'transparent',
                    fontSize: '1rem',
                    minHeight: '56px',
                    touchAction: 'manipulation',
                    WebkitTapHighlightColor: 'transparent',
                    boxShadow: specialFilter === 'newArrivals' ? '0 2px 8px rgba(122, 156, 58, 0.15)' : '0 1px 3px rgba(0,0,0,0.05)',
                    transition: 'all 0.2s ease'
                  }}
                  onClick={() => { 
                    navigate('/catalog?filter=newArrivals');
                    setPage(1);
                    setFilterOpen(false);
                  }}
                >
                  <span style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <span style={{ fontSize: '1.3rem' }}>🌱</span>
                    <span>New Arrivals</span>
                  </span>
                  <span style={{ color: specialFilter === 'newArrivals' ? '#7a9c3a' : '#bbb', fontSize: '1.3rem', fontWeight: 300 }}>{'›'}</span>
                </motion.div>
                <motion.div
                  key="featured"
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: 0.2 }}
                  whileTap={{ scale: 0.97 }}
                  className="filter-option-mobile"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '16px 18px',
                    cursor: 'pointer',
                    background: specialFilter === 'featured' ? 'linear-gradient(135deg, #e9f5e1 0%, #f1f9ed 100%)' : '#fafafa',
                    fontWeight: specialFilter === 'featured' ? 600 : 500,
                    color: specialFilter === 'featured' ? '#2d5016' : '#555',
                    borderRadius: 14,
                    border: '2px solid',
                    borderColor: specialFilter === 'featured' ? '#7a9c3a' : 'transparent',
                    fontSize: '1rem',
                    minHeight: '56px',
                    touchAction: 'manipulation',
                    WebkitTapHighlightColor: 'transparent',
                    boxShadow: specialFilter === 'featured' ? '0 2px 8px rgba(122, 156, 58, 0.15)' : '0 1px 3px rgba(0,0,0,0.05)',
                    transition: 'all 0.2s ease'
                  }}
                  onClick={() => { 
                    navigate('/catalog?filter=featured');
                    setPage(1);
                    setFilterOpen(false);
                  }}
                >
                  <span style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <span style={{ fontSize: '1.3rem' }}>⭐</span>
                    <span>Featured Items</span>
                  </span>
                  <span style={{ color: specialFilter === 'featured' ? '#7a9c3a' : '#bbb', fontSize: '1.3rem', fontWeight: 300 }}>{'›'}</span>
                </motion.div>
                <motion.div
                  key="topSelling"
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: 0.25 }}
                  whileTap={{ scale: 0.97 }}
                  className="filter-option-mobile"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '16px 18px',
                    cursor: 'pointer',
                    background: specialFilter === 'topSelling' ? 'linear-gradient(135deg, #e9f5e1 0%, #f1f9ed 100%)' : '#fafafa',
                    fontWeight: specialFilter === 'topSelling' ? 600 : 500,
                    color: specialFilter === 'topSelling' ? '#2d5016' : '#555',
                    borderRadius: 14,
                    border: '2px solid',
                    borderColor: specialFilter === 'topSelling' ? '#7a9c3a' : 'transparent',
                    fontSize: '1rem',
                    minHeight: '56px',
                    touchAction: 'manipulation',
                    WebkitTapHighlightColor: 'transparent',
                    boxShadow: specialFilter === 'topSelling' ? '0 2px 8px rgba(122, 156, 58, 0.15)' : '0 1px 3px rgba(0,0,0,0.05)',
                    transition: 'all 0.2s ease'
                  }}
                  onClick={() => { 
                    navigate('/catalog?filter=topSelling');
                    setPage(1);
                    setFilterOpen(false);
                  }}
                >
                  <span style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <span style={{ fontSize: '1.3rem' }}>🔥</span>
                    <span>Top Selling</span>
                  </span>
                  <span style={{ color: specialFilter === 'topSelling' ? '#7a9c3a' : '#bbb', fontSize: '1.3rem', fontWeight: 300 }}>{'›'}</span>
                </motion.div>

              </div>
            </motion.div>

            {/* Filters section removed per request */}
            {/* Categories - SECOND */}
            <motion.div 
              className="filter-section"
              style={{ width: '90%', marginBottom: 32, marginTop: 0, position: 'relative' }}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.3 }}
            >
              <h3 style={{ 
                fontWeight: 700, 
                fontSize: '1.15rem', 
                marginBottom: 20, 
                color: '#2d3436',
                letterSpacing: '-0.3px',
                paddingBottom: 0,
                position: 'relative'
              }}>
                Categories
              </h3>
              
              {/* Premium Dropdown */}
              <div style={{ marginTop: 0, position: 'relative' }}>
                <motion.div
                  whileTap={{ scale: 0.98 }}
                  className="category-dropdown-mobile"
                  onClick={() => {
                    setCategoryDropdownOpen(!categoryDropdownOpen);
                    setSortDropdownOpen(false);
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '16px 18px',
                    background: categoryDropdownOpen ? 'linear-gradient(135deg, #f8fdf9 0%, #ffffff 100%)' : '#fafafa',
                    border: '2px solid',
                    borderColor: categoryDropdownOpen ? '#7a9c3a' : 'transparent',
                    borderRadius: 14,
                    cursor: 'pointer',
                    fontSize: '1rem',
                    fontWeight: 600,
                    color: selectedCategory || specialFilter ? '#2d5016' : '#555',
                    boxShadow: categoryDropdownOpen ? '0 4px 12px rgba(122, 156, 58, 0.15)' : '0 1px 3px rgba(0,0,0,0.05)',
                    transition: 'all 0.25s ease',
                    position: 'relative',
                    overflow: 'hidden',
                    minHeight: '56px',
                    touchAction: 'manipulation',
                    WebkitTapHighlightColor: 'transparent'
                  }}
                >
                  <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <rect x="3" y="3" width="7" height="7" rx="1"/>
                      <rect x="14" y="3" width="7" height="7" rx="1"/>
                      <rect x="14" y="14" width="7" height="7" rx="1"/>
                      <rect x="3" y="14" width="7" height="7" rx="1"/>
                    </svg>
                    {selectedCategory || (specialFilter === 'newArrivals' ? 'New Arrivals' : specialFilter === 'featured' ? 'Featured Items' : specialFilter === 'topSelling' ? 'Top Selling' : 'All Products')}
                  </span>
                  <motion.svg
                    animate={{ rotate: categoryDropdownOpen ? 180 : 0 }}
                    transition={{ duration: 0.3 }}
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </motion.svg>
                </motion.div>

                {/* Dropdown Menu */}
                <AnimatePresence>
                  {categoryDropdownOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -10, scale: 0.95 }}
                      transition={{ duration: 0.2, ease: "easeOut" }}
                      style={{
                        position: 'absolute',
                        top: 'calc(100% + 8px)',
                        left: 0,
                        right: 0,
                        background: '#fff',
                        border: '2px solid #e8e8e8',
                        borderRadius: 14,
                        boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                        zIndex: 1000,
                        maxHeight: 400,
                        overflowY: 'auto',
                        padding: '8px'
                      }}
                    >
                      <motion.div
                        whileHover={{ x: 2, backgroundColor: '#f8fff5' }}
                        whileTap={{ scale: 0.97 }}
                        className="category-option-mobile"
                        onClick={() => { 
                          setSelectedCategory(''); 
                          setSpecialFilter('');
                          navigate('/catalog');
                          setPage(1);
                          setCategoryDropdownOpen(false);
                          setFilterOpen(false);
                        }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '14px 16px',
                          marginBottom: 4,
                          cursor: 'pointer',
                          background: selectedCategory === '' && !specialFilter ? '#e9f5e1' : 'transparent',
                          fontWeight: selectedCategory === '' && !specialFilter ? 600 : 500,
                          color: selectedCategory === '' && !specialFilter ? '#2d5016' : '#555',
                          borderRadius: 10,
                          fontSize: '0.95rem',
                          transition: 'all 0.2s ease',
                          minHeight: '52px',
                          touchAction: 'manipulation',
                          WebkitTapHighlightColor: 'transparent'
                        }}
                      >
                        <span>All Products</span>
                        {selectedCategory === '' && !specialFilter && (
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#388e3c" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12"></polyline>
                          </svg>
                        )}
                      </motion.div>
                      
                      {categories.map((cat) => (
                        <motion.div
                          key={cat}
                          whileHover={{ x: 2, backgroundColor: '#f8fff5' }}
                          whileTap={{ scale: 0.97 }}
                          className="category-option-mobile"
                          onClick={() => {
                            setSelectedCategory(cat);
                            setSpecialFilter('');
                            navigate('/catalog');
                            setPage(1);
                            setCategoryDropdownOpen(false);
                            setFilterOpen(false);
                          }}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '14px 16px',
                            marginBottom: 4,
                            cursor: 'pointer',
                            background: selectedCategory === cat && !specialFilter ? '#e9f5e1' : 'transparent',
                            fontWeight: selectedCategory === cat && !specialFilter ? 600 : 500,
                            color: selectedCategory === cat && !specialFilter ? '#2d5016' : '#555',
                            borderRadius: 10,
                            fontSize: '0.95rem',
                            transition: 'all 0.2s ease',
                            minHeight: '52px',
                            touchAction: 'manipulation',
                            WebkitTapHighlightColor: 'transparent'
                          }}
                        >
                          <span>{cat}</span>
                          {selectedCategory === cat && !specialFilter && (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#388e3c" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="20 6 9 17 4 12"></polyline>
                            </svg>
                          )}
                        </motion.div>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>

            {/* Sort By */}
            <motion.div 
              className="filter-section"
              style={{ width: '90%', marginBottom: 24, position: 'relative' }}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.35 }}
            >
              <h4 style={{ 
                fontWeight: 700, 
                fontSize: '1.15rem', 
                marginBottom: 20, 
                color: '#2d3436',
                letterSpacing: '-0.3px',
                paddingBottom: 0,
                position: 'relative'
              }}>
                Sort By
              </h4>
              
              {/* Premium Dropdown */}
              <div style={{ marginTop: 0, position: 'relative' }}>
                <motion.div
                  whileTap={{ scale: 0.98 }}
                  className="sort-dropdown-mobile"
                  onClick={() => {
                    setSortDropdownOpen(!sortDropdownOpen);
                    setCategoryDropdownOpen(false);
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '16px 18px',
                    background: sortDropdownOpen ? 'linear-gradient(135deg, #f8fdf9 0%, #ffffff 100%)' : '#fafafa',
                    border: '2px solid',
                    borderColor: sortDropdownOpen ? '#7a9c3a' : 'transparent',
                    borderRadius: 14,
                    cursor: 'pointer',
                    fontSize: '1rem',
                    fontWeight: 600,
                    color: '#555',
                    boxShadow: sortDropdownOpen ? '0 4px 12px rgba(122, 156, 58, 0.12)' : '0 1px 3px rgba(0,0,0,0.05)',
                    transition: 'all 0.25s ease',
                    minHeight: '56px',
                    touchAction: 'manipulation',
                    WebkitTapHighlightColor: 'transparent'
                  }}
                >
                  <span style={{ display: 'flex', alignItems: 'flex-start', gap: 10, flex: 1, minWidth: 0 }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ flexShrink: 0, marginTop: 2 }}>
                      <line x1="4" y1="6" x2="16" y2="6"></line>
                      <line x1="4" y1="12" x2="13" y2="12"></line>
                      <line x1="4" y1="18" x2="10" y2="18"></line>
                    </svg>
                    <span style={{ lineHeight: '1.4', wordBreak: 'break-word' }}>
                      {sortBy === 'alphabetically-az' ? 'Alphabetically, A-Z' :
                       sortBy === 'alphabetically-za' ? 'Alphabetically, Z-A' :
                       sortBy === 'price-low-high' ? 'Price, low to high' :
                       sortBy === 'price-high-low' ? 'Price, high to low' :
                       sortBy === 'rating-high-low' ? 'Best Rating' :
                       sortBy === 'reviews-high-low' ? 'Most Reviews' :
                       sortBy === 'newest-first' ? 'Newest First' :
                       sortBy === 'on-sale' ? 'On Sale' : 'Alphabetically, A-Z'}
                    </span>
                  </span>
                  <motion.svg
                    animate={{ rotate: sortDropdownOpen ? 180 : 0 }}
                    transition={{ duration: 0.3 }}
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </motion.svg>
                </motion.div>

                {/* Dropdown Menu */}
                <AnimatePresence>
                  {sortDropdownOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -10, scale: 0.95 }}
                      transition={{ duration: 0.2, ease: "easeOut" }}
                      style={{
                        position: 'absolute',
                        top: 'calc(100% + 8px)',
                        left: 0,
                        right: 0,
                        background: '#fff',
                        border: '2px solid #e8e8e8',
                        borderRadius: 14,
                        boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                        zIndex: 1000,
                        maxHeight: 400,
                        overflowY: 'auto',
                        padding: '8px'
                      }}
                    >
                      {[
                        { value: 'alphabetically-az', label: 'Alphabetically, A-Z' },
                        { value: 'alphabetically-za', label: 'Alphabetically, Z-A' },
                        { value: 'price-low-high', label: 'Price, low to high' },
                        { value: 'price-high-low', label: 'Price, high to low' },
                        { value: 'rating-high-low', label: 'Best Rating' },
                        { value: 'reviews-high-low', label: 'Most Reviews' },
                        { value: 'newest-first', label: 'Newest First' },
                        { value: 'on-sale', label: 'On Sale' }
                      ].map((option) => (
                        <motion.div
                          key={option.value}
                          whileHover={{ x: 2, backgroundColor: '#f8fff5' }}
                          whileTap={{ scale: 0.97 }}
                          className="sort-option-mobile"
                          onClick={() => {
                            setSortBy(option.value);
                            setPage(1);
                            setSortDropdownOpen(false);
                            setFilterOpen(false);
                          }}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '14px 16px',
                            marginBottom: 4,
                            cursor: 'pointer',
                            background: sortBy === option.value ? '#e9f5e1' : 'transparent',
                            fontWeight: sortBy === option.value ? 600 : 500,
                            color: sortBy === option.value ? '#2d5016' : '#555',
                            borderRadius: 10,
                            fontSize: '0.95rem',
                            transition: 'all 0.2s ease',
                            minHeight: '52px',
                            touchAction: 'manipulation',
                            WebkitTapHighlightColor: 'transparent'
                          }}
                        >
                          <span>{option.label}</span>
                          {sortBy === option.value && (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#388e3c" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="20 6 9 17 4 12"></polyline>
                            </svg>
                          )}
                        </motion.div>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
            {/* Price Range Filter */}
            <motion.div 
              style={{ width: '90%', marginBottom: 32 }}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.3 }}
            >
              <h4 style={{ 
                fontWeight: 700, 
                fontSize: '1.15rem', 
                marginBottom: 16, 
                color: '#2d3436',
                letterSpacing: '-0.3px',
                paddingBottom: 12,
                borderBottom: '2px solid #f0f0f0',
                position: 'relative'
              }}>
                Price
                <span style={{
                  position: 'absolute',
                  bottom: '-2px',
                  left: 0,
                  width: '100%',
                  height: '3px',
                  background: '#7a9c3a',
                  borderRadius: '2px'
                }}></span>
              </h4>
              <div style={{ fontSize: '0.85rem', color: '#777', marginBottom: 16, marginTop: 16 }}>
                Max price: ₱{maxPriceInDb.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
              <div style={{ display: 'flex', gap: '12px', marginBottom: 10 }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: '0.75rem', color: '#777', marginBottom: 6, display: 'block', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Min</label>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    border: '1px solid #e5e5e5', 
                    borderRadius: 10, 
                    padding: '10px 12px',
                    background: '#fafafa',
                    transition: 'all 0.25s ease'
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = '#7a9c3a';
                    e.currentTarget.style.background = '#fff';
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = '#e5e5e5';
                    e.currentTarget.style.background = '#fafafa';
                  }}
                  >
                    <span style={{ fontSize: '0.9rem', color: '#777', fontWeight: 600 }}>₱</span>
                    <input 
                      type="number"
                      placeholder=""
                      value={minPrice}
                      onChange={(e) => {
                        setMinPrice(e.target.value);
                        setPage(1);
                      }}
                      aria-label="Minimum price filter"
                      style={{
                        border: 'none',
                        outline: 'none',
                        width: '100%',
                        marginLeft: 6,
                        fontSize: '0.9rem',
                        background: 'transparent',
                        fontWeight: 500,
                        color: '#333',
                        minHeight: '40px',
                        touchAction: 'manipulation',
                        appearance: 'none',
                        WebkitAppearance: 'none',
                        WebkitTextFillColor: '#333',
                        caretColor: '#333',
                        paddingRight: '6px',
                        textAlign: 'right'
                      }}
                    />
                  </div>
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: '0.75rem', color: '#777', marginBottom: 6, display: 'block', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Max</label>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    border: '1px solid #e5e5e5', 
                    borderRadius: 10, 
                    padding: '10px 12px',
                    background: '#fafafa',
                    transition: 'all 0.25s ease'
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = '#7a9c3a';
                    e.currentTarget.style.background = '#fff';
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = '#e5e5e5';
                    e.currentTarget.style.background = '#fafafa';
                  }}
                  >
                    <span style={{ fontSize: '0.9rem', color: '#777', fontWeight: 600 }}>₱</span>
                    <input 
                      type="number"
                      placeholder=""
                      value={maxPrice}
                      onChange={(e) => {
                        setMaxPrice(e.target.value);
                        setPage(1);
                      }}
                      aria-label="Maximum price filter"
                      style={{
                        border: 'none',
                        outline: 'none',
                        width: '100%',
                        marginLeft: 6,
                        fontSize: '0.9rem',
                        background: 'transparent',
                        fontWeight: 500,
                        color: '#333',
                        minHeight: '40px',
                        touchAction: 'manipulation',
                        appearance: 'none',
                        WebkitAppearance: 'none',
                        WebkitTextFillColor: '#333',
                        caretColor: '#333',
                        paddingRight: '6px',
                        textAlign: 'right'
                      }}
                    />
                  </div>
                </div>
              </div>
            </motion.div>

          </motion.div>
          {/* Main Content */}
          <div style={{ flex: 1, background: '#fff', borderRadius: 16, boxShadow: '0 2px 12px rgba(0,0,0,0.07)', padding: '32px 2vw', minHeight: 600, width: '100%' }}>
            {/* Product Grid */}
            <AnimatePresence mode="wait">
              <motion.div
                key={`grid-${page}-${selectedCategory}-${sortBy}-${searchQuery}-${inStockOnly}`}
                className="product-grid"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                {loading ? (
                  // Skeleton loading cards
                  Array.from({ length: isMobile ? 12 : 20 }).map((_, idx) => (
                    <div 
                      key={`skeleton-${idx}`}
                      className="catalog-product-card skeleton-card"
                      style={{
                        background: '#fff',
                        borderRadius: 16,
                        padding: '16px',
                        border: '2px solid #f0f0f0',
                        position: 'relative',
                        overflow: 'hidden',
                      }}
                    >
                      {/* Skeleton image */}
                      <div style={{
                        width: '100%',
                        height: '200px',
                        borderRadius: '12px',
                        background: 'linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%)',
                        backgroundSize: '200% 100%',
                        animation: 'shimmer 1.5s infinite',
                        marginBottom: '12px',
                      }} />
                      {/* Skeleton title */}
                      <div style={{
                        width: '80%',
                        height: '20px',
                        borderRadius: '4px',
                        background: 'linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%)',
                        backgroundSize: '200% 100%',
                        animation: 'shimmer 1.5s infinite',
                        marginBottom: '8px',
                      }} />
                      {/* Skeleton stock */}
                      <div style={{
                        width: '40%',
                        height: '16px',
                        borderRadius: '4px',
                        background: 'linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%)',
                        backgroundSize: '200% 100%',
                        animation: 'shimmer 1.5s infinite',
                        marginBottom: '8px',
                      }} />
                      {/* Skeleton price */}
                      <div style={{
                        width: '50%',
                        height: '18px',
                        borderRadius: '4px',
                        background: 'linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%)',
                        backgroundSize: '200% 100%',
                        animation: 'shimmer 1.5s infinite',
                      }} />
                    </div>
                  ))
                ) : products.length === 0 ? (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    style={{
                      gridColumn: '1 / -1',
                      textAlign: 'center',
                      padding: '60px 20px',
                      color: '#666'
                    }}
                  >
                    <div style={{ fontSize: '3rem', marginBottom: '20px' }}>🌱</div>
                    <h3 style={{ fontSize: '1.5rem', marginBottom: '10px', color: '#333' }}>No Products Found</h3>
                    <p style={{ fontSize: '1rem', color: '#888' }}>Try adjusting your filters or search query</p>
                  </motion.div>
                ) : products.map((product, idx) => {
                  return (
                    <motion.div 
                      key={product._id} 
                      className="catalog-product-card" 
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      whileTap={{ scale: 0.98 }}
                      style={{
                        background: '#fff',
                        borderRadius: 20,
                        overflow: 'hidden',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                        transition: 'box-shadow 0.3s ease',
                        cursor: 'pointer'
                      }}
                    >
                    <a 
                      href={`/product/${product._id}`} 
                      aria-label={`View details for ${product.name}`}
                      style={{ width: '100%', textDecoration: 'none', color: 'inherit', display: 'flex', flexDirection: 'column' }}
                    >
                      {/* Image Section */}
                      <div style={{
                        position: 'relative',
                        background: 'linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%)',
                        padding: 24,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '100%',
                        height: 200,
                        overflow: 'visible',
                      }}>
                        {(product.discount && Number(product.discount) > 0) ? (
                          <span style={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            background: 'linear-gradient(135deg, #ff6b00 0%, #ff9500 100%)',
                            color: '#fff',
                            padding: '4px 10px',
                            borderRadius: 16,
                            fontSize: '0.65rem',
                            fontWeight: 700,
                            letterSpacing: '0.5px',
                            zIndex: 10,
                            lineHeight: 1,
                            textTransform: 'uppercase',
                            boxShadow: '0 2px 6px rgba(255,107,0,0.4)'
                          }}>
                            SALE
                          </span>
                        ) : null}
                        <img 
                          src={product.image && product.image.trim() !== '' ? product.image : '/default-plant.png'} 
                          alt={product.name} 
                          style={{ 
                            width: '100%', 
                            height: '100%', 
                            objectFit: 'contain',
                            filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.15))'
                          }} 
                        />
                      </div>

                      {/* Content Section */}
                      <div style={{ 
                        padding: '20px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        textAlign: 'center',
                        background: '#fff'
                      }}>
                        {/* Product Name */}
                        <div style={{ 
                          fontWeight: 600, 
                          fontSize: '1rem', 
                          marginBottom: 8,
                          color: '#1a1a1a',
                          letterSpacing: '0.3px',
                          minHeight: 40,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          {product.name}
                        </div>
                        
                        {/* Description/Stock */}
                        <div style={{ 
                          fontSize: '0.8rem', 
                          color: '#999',
                          marginBottom: 10,
                          minHeight: 40,
                          maxHeight: 40,
                          overflow: 'hidden',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          lineHeight: '20px'
                        }}>
                          {product.description || ''}
                        </div>

                        {/* Rating Hearts */}
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          gap: 3,
                          marginBottom: 12,
                          height: 20
                        }}>
                          {[1, 2, 3, 4, 5].map((rating) => {
                            const avgRating = product.averageRating || 0;
                            const isActive = rating <= Math.round(avgRating);
                            return (
                              <svg key={rating} width="18" height="18" viewBox="0 0 100 100" style={{ flexShrink: 0 }}>
                                <path
                                  d="M50,90 L20,50 Q10,30 20,20 Q30,10 40,20 L50,30 L60,20 Q70,10 80,20 Q90,30 80,50 Z"
                                  fill={isActive ? '#5a9a5a' : '#e2e8f0'}
                                />
                                <path
                                  d="M50,30 Q25,25 20,50 L50,90 Z"
                                  fill={isActive ? '#4a8a4a' : '#d1d5db'}
                                  opacity="0.9"
                                />
                                <path
                                  d="M50,30 Q75,25 80,50 L50,90 Z"
                                  fill={isActive ? '#7ab87a' : '#e5e7eb'}
                                  opacity="0.9"
                                />
                                <path
                                  d="M50,30 L50,15 Q52,8 50,5 Q48,8 50,15 L50,90 Z"
                                  fill={isActive ? '#8bc34a' : '#e8e8e8'}
                                  opacity="0.95"
                                />
                              </svg>
                            );
                          })}
                        </div>

                        {/* Price */}
                        <div style={{ 
                          fontSize: '1.3rem', 
                          fontWeight: 700,
                          color: '#1a1a1a',
                          marginBottom: 16
                        }}>
                          {product.discount && Number(product.discount) > 0 ? (
                            <>
                              <span style={{ textDecoration: 'line-through', color: '#999', fontSize: '0.9rem', marginRight: 8 }}>
                                ₱{Number(product.price).toFixed(2)}
                              </span>
                              ₱{(Number(product.price) - (Number(product.price) * (Number(product.discount) / 100))).toFixed(2)}
                            </>
                          ) : (
                            <>₱{Number(product.price).toFixed(2)}</>
                          )}
                        </div>

                        {/* Add to Cart Button */}
                        <motion.button
                          onClick={(e) => handleQuickAddToCart(e, product._id, product.name)}
                          disabled={addingToCart[product._id] || product.stock === 0}
                          whileHover={{ scale: product.stock === 0 ? 1 : 1.02 }}
                          whileTap={{ scale: product.stock === 0 ? 1 : 0.98 }}
                          style={{
                            width: '100%',
                            padding: '12px',
                            borderRadius: 12,
                            background: product.stock === 0 ? '#e0e0e0' : cartItems.includes(product._id) ? '#6d9e67' : '#7a9c3a',
                            border: 'none',
                            color: product.stock === 0 ? '#999' : '#fff',
                            fontSize: '0.9rem',
                            fontWeight: 600,
                            cursor: product.stock === 0 ? 'not-allowed' : 'pointer',
                            transition: 'all 0.2s ease',
                            opacity: addingToCart[product._id] ? 0.6 : 1,
                            letterSpacing: '0.5px',
                            textTransform: 'uppercase'
                          }}
                          title={product.stock === 0 ? 'Out of stock' : cartItems.includes(product._id) ? 'Remove from cart' : 'Add to cart'}
                        >
                          {addingToCart[product._id] ? (
                            <motion.div
                              animate={{ rotate: 360 }}
                              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                              style={{ fontSize: '0.85rem' }}
                            >
                              ⏳
                            </motion.div>
                          ) : product.stock === 0 ? 'Out of Stock' : cartItems.includes(product._id) ? 'Remove' : 'Add to Cart'}
                        </motion.button>
                      </div>
                    </a>
                  </motion.div>
                );
              })}
              </motion.div>
            </AnimatePresence>
            {/* Pagination */}
            {products.length > 0 && totalPages > 1 && (
              <motion.div 
                style={{ 
                  background: '#fff', 
                  borderRadius: 16, 
                  padding: 20, 
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)', 
                  border: '1.5px solid #f0f0f0', 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center', 
                  flexWrap: 'wrap', 
                  gap: 16, 
                  marginTop: 24 
                }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
              >
                <div style={{ color: '#6b7280', fontSize: '0.9rem', fontWeight: 500 }}>
                  Showing {products.length > 0 ? ((page - 1) * itemsPerPage) + 1 : 0}-{Math.min(page * itemsPerPage, products.length + ((page - 1) * itemsPerPage))} of {totalCount} products
                </div>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <motion.button
                    whileHover={{ scale: page === 1 ? 1 : 1.05 }}
                    whileTap={{ scale: page === 1 ? 1 : 0.95 }}
                    transition={{ duration: 0.2 }}
                    style={{
                      background: page === 1 ? '#f3f4f6' : '#fff',
                      border: '2px solid #e5e7eb',
                      borderRadius: 10,
                      padding: '10px 16px',
                      fontWeight: 600,
                      color: page === 1 ? '#9ca3af' : '#4a5568',
                      cursor: page === 1 ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s ease',
                      minHeight: 44,
                      opacity: page === 1 ? 0.4 : 1,
                      touchAction: 'manipulation',
                      WebkitTapHighlightColor: 'transparent',
                    }}
                    disabled={page === 1}
                    onClick={() => setPage(1)}
                  >
                    First
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: page === 1 ? 1 : 1.05 }}
                    whileTap={{ scale: page === 1 ? 1 : 0.95 }}
                    transition={{ duration: 0.2 }}
                    style={{
                      background: page === 1 ? '#f3f4f6' : '#fff',
                      border: '2px solid #e5e7eb',
                      borderRadius: 10,
                      padding: '10px 16px',
                      fontWeight: 600,
                      color: page === 1 ? '#9ca3af' : '#4a5568',
                      cursor: page === 1 ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s ease',
                      minHeight: 44,
                      opacity: page === 1 ? 0.4 : 1,
                      touchAction: 'manipulation',
                      WebkitTapHighlightColor: 'transparent',
                    }}
                    disabled={page === 1}
                    onClick={() => setPage(prev => Math.max(1, prev - 1))}
                  >
                    Previous
                  </motion.button>
                  {Array.from({ length: totalPages }, (_, i) => {
                    const pageNum = i + 1;
                    if (
                      pageNum === 1 || 
                      pageNum === totalPages || 
                      (pageNum >= page - 1 && pageNum <= page + 1)
                    ) {
                      return (
                        <motion.button
                          key={pageNum}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          transition={{ duration: 0.2 }}
                          style={{
                            background: page === pageNum ? 'linear-gradient(135deg, #7a9c3a 0%, #88ab47 100%)' : '#fff',
                            border: page === pageNum ? '2px solid #7a9c3a' : '2px solid #e5e7eb',
                            borderRadius: 10,
                            padding: '10px 16px',
                            fontWeight: 600,
                            color: page === pageNum ? '#fff' : '#4a5568',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            minHeight: 44,
                            minWidth: 44,
                            touchAction: 'manipulation',
                            WebkitTapHighlightColor: 'transparent',
                            boxShadow: page === pageNum ? '0 2px 8px rgba(122, 156, 58, 0.2)' : 'none',
                          }}
                          onMouseOver={e => {
                            if (page !== pageNum) {
                              e.currentTarget.style.borderColor = '#7a9c3a';
                              e.currentTarget.style.background = '#f7faf4';
                            }
                          }}
                          onMouseOut={e => {
                            if (page !== pageNum) {
                              e.currentTarget.style.borderColor = '#e5e7eb';
                              e.currentTarget.style.background = '#fff';
                            }
                          }}
                          onClick={() => setPage(pageNum)}
                        >
                          {pageNum}
                        </motion.button>
                      );
                    } else if (pageNum === page - 2 || pageNum === page + 2) {
                      return <span key={pageNum} style={{ color: '#9ca3af', padding: '0 8px' }}>...</span>;
                    }
                    return null;
                  })}
                  <motion.button
                    whileHover={{ scale: page === totalPages ? 1 : 1.05 }}
                    whileTap={{ scale: page === totalPages ? 1 : 0.95 }}
                    transition={{ duration: 0.2 }}
                    style={{
                      background: page === totalPages ? '#f3f4f6' : '#fff',
                      border: '2px solid #e5e7eb',
                      borderRadius: 10,
                      padding: '10px 16px',
                      fontWeight: 600,
                      color: page === totalPages ? '#9ca3af' : '#4a5568',
                      cursor: page === totalPages ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s ease',
                      minHeight: 44,
                      opacity: page === totalPages ? 0.4 : 1,
                      touchAction: 'manipulation',
                      WebkitTapHighlightColor: 'transparent',
                    }}
                    disabled={page === totalPages}
                    onClick={() => setPage(prev => Math.min(totalPages, prev + 1))}
                  >
                    Next
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: page === totalPages ? 1 : 1.05 }}
                    whileTap={{ scale: page === totalPages ? 1 : 0.95 }}
                    transition={{ duration: 0.2 }}
                    style={{
                      background: page === totalPages ? '#f3f4f6' : '#fff',
                      border: '2px solid #e5e7eb',
                      borderRadius: 10,
                      padding: '10px 16px',
                      fontWeight: 600,
                      color: page === totalPages ? '#9ca3af' : '#4a5568',
                      cursor: page === totalPages ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s ease',
                      minHeight: 44,
                      opacity: page === totalPages ? 0.4 : 1,
                      touchAction: 'manipulation',
                      WebkitTapHighlightColor: 'transparent',
                    }}
                    disabled={page === totalPages}
                    onClick={() => setPage(totalPages)}
                  >
                    Last
                  </motion.button>
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </div>
      {/* Footer */}
      <footer style={{
        width: '100%',
        background: 'linear-gradient(135deg, #0f4527 0%, #145a34 50%, #0f4527 100%)',
        marginTop: 'auto',
        boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.25)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Decorative overlay */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 30% 50%, rgba(255,255,255,0.03) 0%, transparent 70%)',
          pointerEvents: 'none'
        }} />

        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          padding: '40px 20px 20px',
          position: 'relative',
          zIndex: 1
        }}>
          {/* Main Footer Content */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '40px',
            alignItems: 'start',
            marginBottom: '35px'
          }}>
            {/* Connect Section - Hidden on mobile */}
            {!isMobile && <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <h3 style={{
                color: 'rgba(255, 255, 255, 0.95)',
                fontSize: '0.75rem',
                fontWeight: 700,
                letterSpacing: '2px',
                textTransform: 'uppercase',
                margin: 0,
                marginBottom: '4px'
              }}>Connect With Us</h3>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <a
                  href="https://www.facebook.com/profile.php?id=61552636677232"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Facebook"
                  style={{
                    width: '44px',
                    height: '44px',
                    borderRadius: '12px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(10px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    cursor: 'pointer',
                    border: '1.5px solid rgba(255, 255, 255, 0.15)',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                    textDecoration: 'none'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                    e.currentTarget.style.transform = 'translateY(-3px)';
                    e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.25)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.15)';
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="#ffffff">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </a>
                <a
                  href="https://www.messenger.com/t/61552636677232"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Messenger"
                  style={{
                    width: '44px',
                    height: '44px',
                    borderRadius: '12px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(10px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    cursor: 'pointer',
                    border: '1.5px solid rgba(255, 255, 255, 0.15)',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                    textDecoration: 'none'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                    e.currentTarget.style.transform = 'translateY(-3px)';
                    e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.25)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.15)';
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="#ffffff">
                    <path d="M12 2C6.477 2 2 6.145 2 11.243c0 2.875 1.44 5.449 3.687 7.126V22l3.486-1.914c.93.257 1.913.395 2.827.395 5.523 0 10-4.145 10-9.243S17.523 2 12 2zm.993 12.437l-2.552-2.725-4.983 2.725 5.48-5.813 2.613 2.725 4.922-2.725-5.48 5.813z"/>
                  </svg>
                </a>
                <a
                  href="viber://chat?number=639368983053"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Chat"
                  style={{
                    width: '44px',
                    height: '44px',
                    borderRadius: '12px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(10px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    cursor: 'pointer',
                    border: '1.5px solid rgba(255, 255, 255, 0.15)',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                    textDecoration: 'none'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                    e.currentTarget.style.transform = 'translateY(-3px)';
                    e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.25)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.15)';
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="#ffffff">
                    <path d="M11.4 0C9.473.028 5.333.344 3.02 2.467 1.302 4.187.696 6.7.633 9.817.57 12.933.488 18.776 6.12 20.36h.003l-.004 2.644s-.037.977.61 1.177c.777.242 1.234-.5 1.977-1.303.41-.44.972-1.084 1.397-1.58 3.85.326 6.812-.417 7.15-.526.776-.253 5.176-.816 5.892-6.657.74-6.02-.36-9.83-2.34-11.546-.596-.55-3.006-2.3-8.375-2.323 0 0-.395-.025-1.026-.017z"/>
                  </svg>
                </a>
              </div>
            </div>}

            {/* Logo Section */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '12px',
              justifySelf: 'center',
              marginBottom: isMobile ? '10px' : '0'
            }}>
              <div style={{
                width: '60px',
                height: '60px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.05) 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '2.5px solid rgba(255, 255, 255, 0.25)',
                padding: '10px',
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3), inset 0 2px 4px rgba(255, 255, 255, 0.1)',
                position: 'relative'
              }}>
                <img
                  src="/images/chiqui_logo_white-outline.svg"
                  alt="Chiqui's Garden Logo"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))'
                  }}
                />
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  color: '#ffffff',
                  fontSize: '1rem',
                  fontWeight: 700,
                  letterSpacing: '0.5px',
                  marginBottom: '4px',
                  textShadow: '0 2px 4px rgba(0,0,0,0.3)'
                }}>Chiqui's Garden</div>
                <div style={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  fontSize: '0.75rem',
                  fontWeight: 400
                }}>© {new Date().getFullYear()} All Rights Reserved</div>
              </div>
            </div>

            {/* Location Section - Hidden on mobile */}
            {!isMobile && <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              justifySelf: 'end'
            }}>
              <h3 style={{
                color: 'rgba(255, 255, 255, 0.95)',
                fontSize: '0.75rem',
                fontWeight: 700,
                letterSpacing: '2px',
                textTransform: 'uppercase',
                margin: 0,
                marginBottom: '4px'
              }}>Our Location</h3>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                background: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(10px)',
                padding: '14px 18px',
                borderRadius: '16px',
                border: '1.5px solid rgba(255, 255, 255, 0.15)',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)'
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '10px',
                  background: 'rgba(255, 255, 255, 0.15)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="rgba(255, 255, 255, 0.95)">
                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                  </svg>
                </div>
                <div>
                  <div style={{
                    color: '#ffffff',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    marginBottom: '3px',
                    letterSpacing: '0.3px'
                  }}>Iba Garden</div>
                  <div style={{
                    color: 'rgba(255, 255, 255, 0.8)',
                    fontSize: '0.8rem',
                    lineHeight: '1.3'
                  }}>Pulong Iba, Silang, Cavite</div>
                </div>
              </div>
            </div>}
          </div>

          {/* Divider */}
          <div style={{
            width: '100%',
            height: '1px',
            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
            margin: isMobile ? '20px 0' : '25px 0'
          }} />

          {/* Bottom Links */}
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            alignItems: 'center',
            gap: isMobile ? '8px' : '30px',
            paddingBottom: '10px'
          }}>
            <a
              href="/about"
              style={{
                color: 'rgba(255, 255, 255, 0.85)',
                fontSize: isMobile ? '0.7rem' : '0.85rem',
                fontWeight: 500,
                textDecoration: 'none',
                letterSpacing: '0.5px',
                transition: 'all 0.3s ease',
                padding: isMobile ? '4px 8px' : '6px 12px',
                borderRadius: '6px',
                whiteSpace: 'nowrap'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = '#ffffff';
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = 'rgba(255, 255, 255, 0.85)';
                e.currentTarget.style.background = 'transparent';
              }}
            >
              About Us
            </a>
            <a
              href="/contact"
              style={{
                color: 'rgba(255, 255, 255, 0.85)',
                fontSize: isMobile ? '0.7rem' : '0.85rem',
                fontWeight: 500,
                textDecoration: 'none',
                letterSpacing: '0.5px',
                transition: 'all 0.3s ease',
                padding: isMobile ? '4px 8px' : '6px 12px',
                borderRadius: '6px',
                whiteSpace: 'nowrap'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = '#ffffff';
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = 'rgba(255, 255, 255, 0.85)';
                e.currentTarget.style.background = 'transparent';
              }}
            >
              Contact Info
            </a>
            <a
              href="/return-policy"
              style={{
                color: 'rgba(255, 255, 255, 0.85)',
                fontSize: isMobile ? '0.7rem' : '0.85rem',
                fontWeight: 500,
                textDecoration: 'none',
                letterSpacing: '0.5px',
                transition: 'all 0.3s ease',
                padding: isMobile ? '4px 8px' : '6px 12px',
                borderRadius: '6px',
                whiteSpace: 'nowrap'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = '#ffffff';
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = 'rgba(255, 255, 255, 0.85)';
                e.currentTarget.style.background = 'transparent';
              }}
            >
              Return & Exchange Policy
            </a>
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <a
              href="#"
              onClick={(e) => { e.preventDefault(); setShowTermsModal(true); }}
              style={{
                color: 'rgba(255, 255, 255, 0.85)',
                fontSize: isMobile ? '0.7rem' : '0.85rem',
                fontWeight: 500,
                textDecoration: 'none',
                letterSpacing: '0.5px',
                transition: 'all 0.3s ease',
                padding: isMobile ? '4px 8px' : '6px 12px',
                borderRadius: '6px',
                whiteSpace: 'nowrap',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = '#ffffff';
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = 'rgba(255, 255, 255, 0.85)';
                e.currentTarget.style.background = 'transparent';
              }}
            >
              Terms of Use
            </a>
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <a
              href="#"
              onClick={(e) => { e.preventDefault(); setShowPrivacyModal(true); }}
              style={{
                color: 'rgba(255, 255, 255, 0.85)',
                fontSize: isMobile ? '0.7rem' : '0.85rem',
                fontWeight: 500,
                textDecoration: 'none',
                letterSpacing: '0.5px',
                transition: 'all 0.3s ease',
                padding: isMobile ? '4px 8px' : '6px 12px',
                borderRadius: '6px',
                whiteSpace: 'nowrap',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = '#ffffff';
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = 'rgba(255, 255, 255, 0.85)';
                e.currentTarget.style.background = 'transparent';
              }}
            >
              Privacy Policy
            </a>
          </div>
        </div>
      </footer>
      </div>
      
      <TermsModal
        isOpen={showTermsModal}
        onClose={() => setShowTermsModal(false)}
      />

      <TermsModal
        isOpen={showPrivacyModal}
        onClose={() => setShowPrivacyModal(false)}
        isPrivacyPolicy={true}
      />

      {showToast && <Toast message={toastMessage} onClose={() => setShowToast(false)} />}
      <ConfirmModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onConfirm={() => navigate('/login')}
        title="Login Required"
        message="Please login to add items to your cart"
        confirmText="Go to Login"
        cancelText="Cancel"
        type="info"
      />
    </div>
  );
}

export default Catalog;
